import { useCallback, useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Textarea } from "./styles";
import { Description, FlexContainer, Subtitle } from "../../../styles";
import { ErrorMessage } from "../../../../../components/ControlledInputs/input";
import Button from "../../Button";
import api from "../../../../../service";
import generateHeaders from "../../../../../utils/generateHeaders";
import { updateRemaningAttempts } from "../../../../../redux/modules/AI_Creator/actions";
import useNotification from "../../../../../hooks/newUXNotification";

const PromptSchema = z.object({
  prompt: z.string().min(1),
});

export default function ImageGenerator({
  setGeneratedImages,
  scrollToRef,
  handleAwaitingAIBasedImageGeneration,
  awaitingAIBasedImageGeneration = false,
}) {
  return (
    <div>
      <Subtitle marginTop="32px" marginBottom="0">
        <FormattedMessage id={"ai_creator.image.label.AI"} />
      </Subtitle>
      <Description marginBottom="16px" marginTop="8px" generate>
        <span>
          <FormattedMessage id="ai_creator.image.AI.desc1" />
        </span>
      </Description>

      <PromptForm
        setGeneratedImages={setGeneratedImages}
        scrollToRef={scrollToRef}
        handleAwaitingAIBasedImageGeneration={
          handleAwaitingAIBasedImageGeneration
        }
        awaitingAIBasedImageGeneration={awaitingAIBasedImageGeneration}
      />
    </div>
  );
}

function PromptForm({
  setGeneratedImages,
  scrollToRef,
  handleAwaitingAIBasedImageGeneration,
  awaitingAIBasedImageGeneration = false,
}) {
  const { unlimitedImages } = useSelector((state) => state.ai_creator);
  const notification = useNotification();
  const { token } = useSelector((state) => state.auth);
  const { contract, event } = useSelector((state) => state.initialSettings);
  const intl = useIntl();
  const form = useForm({
    resolver: zodResolver(PromptSchema),
    defaultValues: {
      prompt: "",
    },
  });
  const { remainingAttempts } = useSelector((state) => state.ai_creator);
  const dispatch = useDispatch();
  const { control, handleSubmit, formState, watch, reset } = form;

  // ============================================================================
  // 🔄 FUNÇÃO DE POLLING - NOVA
  // ============================================================================
  const pollImageStatus = useCallback(
    async (uuid, imageId) => {
      const options = generateHeaders(token);
      const maxAttempts = 60; // 2 minutos (60 * 2s)
      let attempts = 0;

      while (attempts < maxAttempts) {
        attempts++;

        // Aguarda 2 segundos antes de consultar
        await new Promise((resolve) => setTimeout(resolve, 2000));

        try {
          const statusResponse = await api.get(
            `/media-kit/status/${uuid}`,
            options
          );

          const { status, imageUrl, errorMessage } = statusResponse.data;

          // ✅ Imagem pronta
          if (status === "PROCESSED") {
            setGeneratedImages((prev) =>
              prev.map((image) =>
                image.id === imageId
                  ? { ...image, src: imageUrl, generating: false }
                  : image
              )
            );
            return { success: true, url: imageUrl };
          }

          // ❌ Erro na geração
          if (status === "ERROR") {
            throw new Error(errorMessage || "Image generation failed");
          }

          // ⏳ Ainda processando (WAITING ou PENDING) - continua o loop
        } catch (error) {
          console.error("Error polling status:", error);
          throw error;
        }
      }

      // Timeout - levou muito tempo
      throw new Error("Timeout: Image generation took too long");
    },
    [token, setGeneratedImages]
  );

  // ============================================================================
  // FUNÇÃO DE SUBMIT - MODIFICADA PARA ASSÍNCRONO
  // ============================================================================
  const onSubmit = async ({ prompt }) => {
    handleAwaitingAIBasedImageGeneration(true);
    const randomId = Math.floor(Math.random() * 1_000_000);

    try {
      const options = generateHeaders(token);
      const body = JSON.stringify({
        eventId: event.value,
        opportunityId: contract.value,
        prompt,
      });
      scrollToRef();

      // Adiciona imagem com status "gerando"
      setGeneratedImages((prev) => [
        ...prev,
        {
          id: randomId,
          src: undefined,
          error: false,
          generating: true,
        },
      ]);

      // MUDANÇA 1: Agora espera HTTP 202 ACCEPTED ao invés de 200
      const response = await api.post("/media-kit/generate", body, options);

      // MUDANÇA 2: Verifica se é resposta assíncrona
      if (response.status === 202 && response.data.async) {
        const { uuid, remainingAttempts: remaining } = response.data;

        // Atualiza tentativas restantes imediatamente
        dispatch(updateRemaningAttempts({ number: remaining }));

        // MUDANÇA 3: Inicia polling em background
        pollImageStatus(uuid, randomId)
          .then(({ url }) => {
            notification.displaySuccess(
              intl.formatMessage({
                id: "ai_creator.image.generating_image.toast.success",
              })
            );

            handleAwaitingAIBasedImageGeneration(false);
          })
          .catch((error) => {
            // Erro durante polling
            setGeneratedImages((prev) =>
              prev.map((image) =>
                image.id === randomId
                  ? { ...image, error: true, generating: false }
                  : image
              )
            );

            notification.displayError(
              intl.formatMessage({
                id: "ai_creator.image.generating_image.toast.failed",
              })
            );

            handleAwaitingAIBasedImageGeneration(false);
          });

        // Reset do form pode ser feito imediatamente
        reset();
      } else {
        // Fallback: Se por algum motivo vier resposta síncrona (200 OK)
        if (response.status !== 200) {
          throw new Error("Failed to generate image");
        }

        setGeneratedImages((prev) =>
          prev.map((image) =>
            image.id === randomId
              ? { ...image, src: response.data.url, generating: false }
              : image
          )
        );

        dispatch(
          updateRemaningAttempts({ number: response.data.remainingAttempts })
        );

        reset();
        handleAwaitingAIBasedImageGeneration(false);
      }
    } catch (error) {
      // Erro na requisição inicial
      setGeneratedImages((prev) =>
        prev.map((image) =>
          image.id === randomId
            ? { ...image, error: true, generating: false }
            : image
        )
      );

      notification.displayError(
        intl.formatMessage({
          id: "ai_creator.image.generating_image.toast.failed",
        })
      );

      handleAwaitingAIBasedImageGeneration(false);
    }
  };

  const errorMessage = formState?.errors?.prompt?.message;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name={"prompt"}
        control={control}
        render={({ field }) => (
          <Textarea
            disabled={formState.isSubmitting || awaitingAIBasedImageGeneration}
            error={errorMessage}
            autoSize={{ minRows: 4, maxRows: 6 }}
            {...field}
            placeholder={intl.formatMessage({
              id: "ai_creator.image.generate.placeholder",
            })}
            onChange={(e) => field.onChange(e)}
            onPressEnter={(e) => {
              e.preventDefault();
              if (
                (watch("prompt").length === 0 || remainingAttempts === 0) &&
                !unlimitedImages
              )
                return;

              handleSubmit(onSubmit)();
            }}
          />
        )}
      />
      <ErrorMessage errorMessage={errorMessage} />

      <FlexContainer spaceBetween center marginTop="8px" marginBottom="16px">
        <Description marginBottom="0" marginTop="0" generateDesc>
          <FormattedMessage id="ai_creator.image.AI.prompt.example" />
        </Description>
        <FlexContainer width="220px">
          <Button
            label={
              formState.isSubmitting || awaitingAIBasedImageGeneration
                ? "ai_creator.image.generating_image"
                : "ai_creator.image.generate"
            }
            disabled={
              ((watch("prompt").length === 0 || remainingAttempts === 0) &&
                !unlimitedImages) ||
              awaitingAIBasedImageGeneration
            }
            onClick={handleSubmit(onSubmit)}
            loading={formState.isSubmitting || awaitingAIBasedImageGeneration}
          />
        </FlexContainer>
      </FlexContainer>
    </form>
  );
}
